import { SiteShell } from "@/components/site/SiteShell";
import { PageHero } from "@/components/site/PageHero";
import { SEO, breadcrumbSchema } from "@/components/site/SEO";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Globe2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ABOUT_IMAGE, TEAM, STATS } from "@/lib/siteData";
import { ROUTE_SEO } from "@/lib/routeSeo";

export default function About() {
  return (
    <SiteShell>
      <SEO
        title={ROUTE_SEO["/about"].title}
        description={ROUTE_SEO["/about"].meta}
        path="/about"
        jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About" }])}
      />
      <PageHero
        kicker="About"
        title={ROUTE_SEO["/about"].h1}
        subtitle="Over a decade of craft. Hundreds of productions delivered. A quiet obsession with how a single frame can move a brand forward."
        breadcrumb={[{ name: "Home", path: "/" }, { name: "About" }]}
      />
      <section className="section-pad">
        <div className="container-site grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-5 text-sm sm:text-base text-muted-foreground leading-relaxed">
            <p>Pie in the Sky Productions is a full-service video production company in Cape Town. We were founded by storytellers - not technicians - and that shapes every brief we take on. The reason a frame works is rarely the kit. It’s the decision behind the kit.</p>
            <p>We combine award-winning filmmakers, cinematographers and a dedicated in-house post-production team. From a single-shooter day to a multi-territory campaign, the same senior judgement carries from first concept to final delivery.</p>
            <p>Clients include Takealot, BMW, Woolworths, McDonald’s, Manchester City, IHG, Cunard, Crystal Cruises, Roche, Austell, Lanzerac and more - across South Africa, Europe, the UK and the Middle East.</p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button asChild className="h-11 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 px-5"><Link to="/contact">Work with us <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
              <Button asChild variant="ghost" className="h-11 rounded-full border border-border bg-card/60 hover:bg-card px-5"><Link to="/crew">Meet the crew</Link></Button>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="relative overflow-hidden rounded-2xl border border-border">
              <img src={ABOUT_IMAGE} alt="Pie in the Sky Productions film crew on a Cape Town commercial shoot" className="w-full h-[320px] sm:h-[460px] object-cover" />
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 text-white">
                <div className="font-mono-credits text-[10px] uppercase tracking-[0.3em] text-white/70">On set</div>
                <div className="font-display text-2xl uppercase tracking-[0.02em]">Essential Flavours - Cape Town</div>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-border pt-8">
              {STATS.map((s) => (<div key={s.label}><div className="font-display text-3xl sm:text-4xl text-foreground">{s.value}</div><div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-1">{s.label}</div></div>))}
            </div>
          </div>
        </div>
      </section>
      <section className="section-pad bg-[hsl(225,22%,4%)] border-y border-border">
        <div className="container-site">
          <p className="kicker mb-3">The team</p>
          <h2 className="font-display uppercase text-foreground text-[clamp(2rem,5vw,3.5rem)] leading-[0.95]">Senior craft. Friendly humans.</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TEAM.map((m, i) => (
              <motion.div key={m.name} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }} className="group">
                <div className="aspect-[4/5] overflow-hidden rounded-xl border border-border bg-card">
                  <img src={m.image} alt={m.name} className="h-full w-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-[1.04]" loading="lazy" />
                </div>
                <div className="mt-4">
                  <div className="font-display text-lg uppercase tracking-[0.02em] text-foreground">{m.name}</div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-1">{m.role}</div>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{m.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <section className="section-pad">
        <div className="container-site grid grid-cols-1 md:grid-cols-3 gap-6">
          {[{Icon: Award, t: "Award-winning", d: "Listed by DesignRush among Cape Town’s top video production companies."},{Icon: Globe2, t: "Globally produced", d: "Shoots across four continents - cruise, hotel, automotive, healthcare, sport."},{Icon: Sparkles, t: "In-house finishing", d: "Calibrated grading suite, sound design, motion graphics - all under one roof."}].map(({Icon, t, d}) => (
            <div key={t} className="rounded-2xl border border-border bg-card p-8">
              <Icon className="h-6 w-6 text-[hsl(var(--primary))]" />
              <h3 className="mt-5 font-display text-2xl uppercase tracking-[0.02em] text-foreground">{t}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>
    </SiteShell>
  );
}
